import { useEffect, useRef, useState } from 'react';
import { ScreenViewEvent, useScreenViewEvent } from '@stitch-fix/event-reporter';
import { Button, Modal, Text, Stack, Link, Spinner } from '@stitch-fix/mode-react';
import { SCREEN_VIEW_MAP } from '../constants';
export type VisionConsentModalProps = {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isConfirming?: boolean;
};
const ModalContent = () => <Stack spacing={1.5} mt={1}>
    <Text setting="body-medium">
      Stitch Fix Vision uses the photos you upload to personalize your
      experience, share with your Stylist and generate images meant to resemble
      you. To generate these images, we use AI to analyze your facial geometry.
      Your facial geometry is shared with our service providers to make this
      happen, and will be retained for up to 30 days after image generation.
    </Text>
    <Text setting="body-medium">
      By clicking “I agree,” below, you consent to the use of your photo and
      facial geometry as further described in the{' '}
      <Link href="https://www.stitchfix.com/terms/imagegennotice" target="_blank" rel="noopener noreferrer">
        Stitch Fix Vision Information Notice
      </Link>
      .
    </Text>
  </Stack>;

// Wrapper component that tracks screen view event
const TrackedModalContent = () => {
  const {
    EventProvider
  } = useScreenViewEvent({
    schema: 'screen_view' as const,
    name: SCREEN_VIEW_MAP.consent as ScreenViewEvent['name'],
    source_app: 'categories-next-ui' as const,
    content_id: 'vision-onboarding-improvements-may-2026'
  });
  return <EventProvider>
      <ModalContent />
    </EventProvider>;
};
export const VisionConsentModal = ({
  isOpen,
  onConfirm,
  onCancel,
  isConfirming = false
}: VisionConsentModalProps) => {
  const hasTrackedRef = useRef(false);
  const [shouldTrack, setShouldTrack] = useState(false);
  useEffect(() => {
    if (isOpen && !hasTrackedRef.current) {
      setShouldTrack(true);
      hasTrackedRef.current = true;
    }
    if (!isOpen) {
      hasTrackedRef.current = false;
      setShouldTrack(false);
    }
  }, [isOpen]);
  return <Modal isOpen={isOpen} heading={<Text setting="title-medium" color="blue-45" as="div">
          A note about privacy
        </Text>} variant="slide-up-small" onClose={isConfirming ? undefined : onCancel} footer={<Stack spacing={1}>
          <Button width="fill" onClick={onConfirm} disabled={isConfirming} endIcon={isConfirming ? <Spinner variant="process" size="xsmall" /> : null}>
            I agree
          </Button>
          <Button width="fill" variant="outline" onClick={onCancel} disabled={isConfirming}>
            Go back
          </Button>
        </Stack>}>
      {shouldTrack ? <TrackedModalContent /> : <ModalContent />}
    </Modal>;
};